/**
 * SOVRANOVA — sessionStore
 *
 * Keeps the live conversation on the device so that losing the page costs
 * nothing.
 *
 * THE PROBLEM THIS SOLVES: a phone locks the screen, the browser reclaims the
 * page, and React remounts with empty state. Before this, that meant returning
 * to a blank "Begin Session" — as if the conversation had never happened,
 * moments after someone had said the hardest thing they had to say. Having to
 * start that over is not a minor inconvenience; it is the reason a person
 * doesn't come back.
 *
 * TWO LAYERS, DELIBERATELY:
 *   - this file is the FAST path: written synchronously on every turn, so it
 *     survives a page kill even with no network at all.
 *   - GET /api/session/open is the TRUTH: it recovers the same conversation on
 *     a new device, after a cache clear, or after a reinstall.
 * If they disagree, the server wins — the local copy can be stale, but it can
 * never be more complete than what was actually written.
 *
 * Nothing here is a substitute for the server write. Turns are already
 * persisted server-side as they happen; this is purely about the UI being able
 * to find its way back.
 */

const KEY = 'sov_open_session_v1'

// OWNER SCOPING (pre-beta privacy wave, 4 Sep 2026). The saved conversation is
// plaintext in localStorage and used to carry no owner: on a shared device the
// next account to sign in was offered the previous person's turns. Every save
// now records the signed-in user id (set by useAuth via setSessionOwner), a
// load for a different user discards the record, and useAuth clears it on
// SIGNED_OUT. Server-side history is untouched — this is the device copy only.
let currentOwner = null
export function setSessionOwner(uid) { currentOwner = uid || null }
// Older than this and we don't offer to resume — being dropped back into
// yesterday's heavy conversation unannounced would be its own harm.
const MAX_AGE_MS = 24 * 60 * 60 * 1000

function safeParse(raw) {
  try { return JSON.parse(raw) } catch { return null }
}

/** Write the current conversation. Cheap, called on every change. */
export function saveOpenSession({ sessionId, sessionType, mode, messages, draft }) {
  if (typeof localStorage === 'undefined') return
  if (!sessionId) return
  try {
    localStorage.setItem(KEY, JSON.stringify({
      v: 1,
      uid: currentOwner,
      sessionId,
      sessionType: sessionType || 'REFLECTION',
      mode: mode || 'individual',
      // Store only what is needed to rebuild the screen. Audio payloads are
      // deliberately dropped — they're large, they're re-fetchable, and they
      // would blow the storage quota inside a long session.
      messages: (messages || []).map(m => ({
        id: m.id, role: m.role, content: m.content, agent: m.agent || null,
      })),
      draft: draft || '',
      at: Date.now(),
    }))
  } catch {
    // Quota exceeded or storage disabled (private mode). Not fatal: the server
    // still has every turn, and /session/open will recover it.
  }
}

/** Read back a recent unfinished conversation, or null. */
export function loadOpenSession() {
  if (typeof localStorage === 'undefined') return null
  const saved = safeParse(localStorage.getItem(KEY))
  if (!saved || saved.v !== 1 || !saved.sessionId) return null
  // Not this person's conversation (or an owner-less legacy record while someone
  // is signed in): never offer it, and drop it so it cannot resurface.
  if (currentOwner && saved.uid !== currentOwner) { clearOpenSession(); return null }
  if (!saved.at || Date.now() - saved.at > MAX_AGE_MS) { clearOpenSession(); return null }
  if (!Array.isArray(saved.messages) || !saved.messages.some(m => m.role === 'user')) return null
  return saved
}

/** Forget it — on a clean close, or once the person declines to resume. */
export function clearOpenSession() {
  if (typeof localStorage === 'undefined') return
  try { localStorage.removeItem(KEY) } catch { /* nothing to do */ }
}

/**
 * Merge the device copy with the server copy, preferring whichever actually has
 * more of the conversation. The local cache can be a turn or two ahead if the
 * page died mid-request, and the server can be ahead if the device was cleared.
 */
export function reconcile(local, remote) {
  if (!remote) return local || null
  if (!local || local.sessionId !== remote.sessionId) {
    return { ...remote, mode: 'individual', draft: '' }
  }
  const richer = (remote.messages?.length || 0) >= (local.messages?.length || 0) ? remote : local
  return { ...local, ...remote, messages: richer.messages, draft: local.draft || '' }
}
